import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import QuickAddPartyModal from "../../billingscreen/QuickAddPartyModal";
import { createManualCreditBill } from "../../services/creditDueService";

const CARE_OF_OPTIONS = ["RAHITH", "JALAL", "VISHNU", "OTHERS"];

function partyFromCreated(p) {
  return {
    id: p.id,
    partyName: p.partyName || p.name || "",
    phone: p.phone || "",
    group: p.group || "Sundry Debtors",
  };
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function newRow() {
  return { key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, invoiceNo: "", invoiceDate: todayISO(), amount: "" };
}

/**
 * Shared form for manual Udhar credit — use inside a page or modal.
 * @param {{ onSuccess?: () => void, branch?: string }} props
 */
export default function CreditAddCreditContent({ onSuccess, branch = "Main" }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [rows, setRows] = useState(() => [newRow()]);
  const [note, setNote] = useState("");
  /** Required care-of selection (c/o) for this manual credit entry */
  const [careOf, setCareOf] = useState("");
  const [saving, setSaving] = useState(false);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);

  const loadCustomers = useCallback(async () => {
    try {
      const snap = await getDocs(query(collection(db, "customers"), where("group", "==", "Sundry Debtors")));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) =>
        String(a.partyName || a.name || "").localeCompare(String(b.partyName || b.name || ""), undefined, { sensitivity: "base" })
      );
      setCustomers(list);
    } catch (e) {
      console.error(e);
      toast.error("Could not load customers.");
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const applyCreatedCustomer = (raw) => {
    const party = partyFromCreated(raw);
    setCustomers((prev) => {
      if (prev.some((c) => c.id === party.id)) return prev;
      const next = [...prev, { id: party.id, partyName: party.partyName, phone: party.phone, group: party.group }];
      next.sort((a, b) =>
        String(a.partyName || a.name || "").localeCompare(String(b.partyName || b.name || ""), undefined, { sensitivity: "base" })
      );
      return next;
    });
    setCustomerId(party.id);
    setCreateCustomerOpen(false);
  };

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, newRow()]);
  const removeRow = (key) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerId) return toast.error("Select a customer.");
    const party = customers.find((c) => c.id === customerId);
    if (!party) return toast.error("Select a customer.");
    const customerName = party.partyName || party.name || "";
    const phone = party.phone || "";

    if (!careOf) return toast.error("Select c/o.");

    const validRows = [];
    for (const r of rows) {
      const inv = String(r.invoiceNo || "").trim();
      const amt = parseFloat(r.amount || "0");
      const invDate = String(r.invoiceDate || "").trim();
      if (!inv) return toast.error("Enter invoice number for every line.");
      if (!invDate) return toast.error("Enter invoice date for every line.");
      if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a valid amount on every line.");
      validRows.push({ invoiceNo: inv, invoiceDate: invDate, totalAmount: amt });
    }

    setSaving(true);
    try {
      for (const r of validRows) {
        await createManualCreditBill({
          customerId,
          customerName,
          phone,
          totalAmount: r.totalAmount,
          invoiceDate: r.invoiceDate,
          note,
          invoiceNo: r.invoiceNo,
          careOf: careOf.trim(),
          branch,
        });
      }
      toast.success(validRows.length > 1 ? `${validRows.length} credit bills added.` : "Credit bill added.");
      setRows([newRow()]);
      setNote("");
      setCareOf("");
      onSuccess?.();
    } catch (err) {
      toast.error(err?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-slate-600">
          Opening balance or credit without a stock invoice. For product sales use{" "}
          <Link className="text-indigo-600 underline" to="/billing" onClick={(e) => e.stopPropagation()}>
            Outward Supply
          </Link>{" "}
          → Credit sale.
        </p>

        <div>
          <label htmlFor="credit-customer-select" className="text-sm font-semibold text-slate-800">
            Customer <span className="text-red-600" aria-hidden="true">*</span>
          </label>
          <p id="credit-customer-hint" className="mt-0.5 text-xs text-slate-500">
            Required — pick from the list or create a new customer.
          </p>
          <div className="mt-2 flex flex-wrap items-stretch gap-2 sm:items-center">
            <select
              id="credit-customer-select"
              required
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
              aria-describedby="credit-customer-hint"
            >
              <option value="">— Select customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.partyName || c.name || c.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setCreateCustomerOpen(true)}
              className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
            >
              New customer
            </button>
          </div>
        </div>

        <label className="block text-sm font-semibold text-slate-800">
          C/o <span className="text-red-600" aria-hidden="true">*</span>
          <select
            required
            value={careOf}
            onChange={(e) => setCareOf(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">— Select c/o —</option>
            {CARE_OF_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="mt-0.5 block text-xs text-slate-500">
            Required. Shown on bills for reference; does not replace the selected customer.
          </span>
        </label>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-800">Invoices</p>
            <button
              type="button"
              onClick={addRow}
              className="text-xs font-bold text-indigo-600 hover:underline"
            >
              + Add another invoice
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-bold uppercase text-slate-600">
                <tr>
                  <th className="p-2">Invoice / bill no.</th>
                  <th className="p-2 whitespace-nowrap">Invoice date</th>
                  <th className="p-2 text-right">Amount (₹)</th>
                  <th className="p-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-slate-100">
                    <td className="p-2 align-top">
                      <input
                        type="text"
                        required
                        value={r.invoiceNo}
                        onChange={(e) => updateRow(r.key, "invoiceNo", e.target.value)}
                        className="w-full min-w-[8rem] rounded border border-slate-300 px-2 py-1.5 text-xs font-mono"
                        placeholder="e.g. SINV-2526-0001"
                        autoComplete="off"
                      />
                    </td>
                    <td className="p-2 align-top whitespace-nowrap">
                      <input
                        type="date"
                        required
                        value={r.invoiceDate}
                        onChange={(e) => updateRow(r.key, "invoiceDate", e.target.value)}
                        className="rounded border border-slate-300 px-2 py-1.5 text-xs"
                      />
                    </td>
                    <td className="p-2 align-top">
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        required
                        value={r.amount}
                        onChange={(e) => updateRow(r.key, "amount", e.target.value)}
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs text-right tabular-nums"
                      />
                    </td>
                    <td className="p-2 align-top">
                      {rows.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => removeRow(r.key)}
                          className="text-xs font-semibold text-red-600 hover:underline"
                          aria-label="Remove row"
                        >
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <label className="block text-sm font-semibold text-slate-800">
          Note (optional, applies to all lines)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={saving || !customerId}
          title={!customerId ? "Select or create a customer first" : undefined}
          className="w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Saving…" : rows.length > 1 ? `Save ${rows.length} credit bills` : "Save credit bill"}
        </button>
      </form>

      <QuickAddPartyModal
        open={createCustomerOpen}
        onClose={() => setCreateCustomerOpen(false)}
        onCreated={applyCreatedCustomer}
        title="New customer"
        overlayClassName="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
      />
    </>
  );
}
